import { AuthContext } from '@/contexts';
import { Conversation, Role } from '@/types';
import { apiUrl, CONVERSATION_LIST_KEY, fetchWithAuth } from '@/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/shadcn/dropdown-menu';
import { useRTL } from '@/hooks';
import { Ellipsis } from 'lucide-react';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteAlertDialog } from '../DeleteAlertDialog';
import { toast } from 'sonner';

interface Props {
  conversation: Conversation;
  onViewConversations: () => void;
}

export function ConversationsActionCell({ conversation, onViewConversations }: Props) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const { role } = useContext(AuthContext);
  const isAdmin = role === Role.ADMIN;

  // NOTE: we must control the open state here since shadcn has an issue with the combination of dialog and dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const deleteConversation = async () => {
    return fetchWithAuth(`${apiUrl}/conversation/${conversation.chatId}/${conversation.username}`, {
      method: 'DELETE',
    });
  };

  const mutation = useMutation({
    mutationFn: deleteConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONVERSATION_LIST_KEY, conversation.chatId] });
      toast.message(t('dashboard.conversations.deleteConversationSuccess'));
    },
    onError: (error) => {
      toast.error(t('dashboard.conversations.deleteConversationError'));
      console.error('deleteConversation: ', error);
    },
  });

  return (
    <div>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen} dir={isRTL ? 'rtl' : 'ltr'}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <Ellipsis size={17} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel hidden>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => {
              setDropdownOpen(false);
              onViewConversations();
            }}
          >
            {t('dashboard.conversations.viewConversations')}
          </DropdownMenuItem>

          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DeleteAlertDialog
                title={t('dashboard.conversations.deleteDialog.title')}
                description={t('dashboard.conversations.deleteDialog.description')}
                confirmText={t('dashboard.conversations.deleteDialog.confirmText')}
                checkboxText={t('dashboard.conversations.deleteDialog.checkboxText')}
                onConfirm={mutation.mutate}
              >
                <DropdownMenuItem
                  className="text-destructive hover:text-destructive!"
                  onSelect={(e) => {
                    e.preventDefault();
                  }}
                >
                  {t('dashboard.conversations.deleteConversation')}
                </DropdownMenuItem>
              </DeleteAlertDialog>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
